import { Injectable, inject } from '@angular/core';
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from './authentication.service';

/** Clave donde se recuerda la ruta que el usuario intentaba abrir. */
export const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';

/**
 * Bloquea las rutas privadas si no hay sesión de Cognito activa.
 *
 * Este guard es el punto de aterrizaje del caso "recargué la pestaña y salió
 * el XML de AccessDenied": una vez que CloudFront devuelve `/index.html` para
 * rutas profundas (ver `docs/DEPLOY_SPA_S3.md`), Angular arranca, el guard no
 * encuentra sesión válida y manda al login en lugar de dejar al usuario en la
 * página de error de S3.
 *
 * Dos cosas que antes no hacía:
 *
 *  1. `logOut()`. Solo navegaba a '/', dejando en localStorage tokens ya
 *     caducados (`idToken`, `userId`, `orgId`…) que el código heredado sigue
 *     leyendo directo. Resultado: pantalla de login pero con datos del usuario
 *     anterior colgando en memoria, y peticiones que salían con un Bearer
 *     muerto.
 *  2. Recordar el destino. Ahora guarda la URL que se quería abrir para que el
 *     login devuelva al usuario ahí en vez de siempre al tablero.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationGuard implements CanActivate {
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const isLoggedIn = await this.authenticationService.isLoggedIn();
    if (isLoggedIn) return true;

    // Sesión ausente o expirada: cerrarla de verdad antes de redirigir.
    // `logOut()` ya limpia los keys heredados de localStorage en su `finally`,
    // así que aunque Cognito falle (offline, token corrupto) el estado local
    // queda limpio.
    await this.authenticationService.logOut().catch(() => undefined);

    this.rememberTarget(state.url);
    this.router.navigate(['/']);
    return false;
  }

  /**
   * Guarda la ruta destino en sessionStorage (no localStorage: debe morir con
   * la pestaña). Se ignoran las rutas que ya son el login para no dejar un
   * redirect circular.
   */
  private rememberTarget(url: string | null | undefined): void {
    if (!url) return;
    const clean = url.split('?')[0];
    if (clean === '/' || clean === '' || clean === '/login') return;
    try {
      sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, url);
    } catch {
      // Modo incógnito con storage bloqueado: no es crítico, seguimos.
    }
  }
}
